<template>
  <aside
    :class="{ 'banner--mounted': mounted }"
    :style="{
      '--banner-color-background': colors.background,
      '--banner-color-text': colors.text
    }"
  >
    <p v-if="this.static">
      <template v-for="content in contents">
        <announcement-banner-content v-bind="content" :key="content.id" />
      </template>
    </p>
    <ul v-else>
      <template v-for="n in iterations">
        <li
          v-for="content in contents"
          :key="`${n}:${content.id}`"
          :aria-hidden="n > 1 ? 'true' : null"
        >
          <announcement-banner-content v-bind="content" />
        </li>
      </template>
    </ul>
    <hr role="presentation" />
  </aside>
</template>

<script>
import AnnouncementBannerContent from '~/components/announcement-banner-content'

export default {
  name: 'AnnouncementBanner',

  components: {
    AnnouncementBannerContent
  },

  props: {
    contents: {
      default: () => [],
      type: Array
    },
    colors: {
      default: () => ({ background: '#1d2a58', text: '#ffffff' }),
      type: Object
    },
    static: {
      default: false,
      type: Boolean
    },
    visibility: {
      default: null,
      type: Object
    }
  },

  data() {
    return {
      iterations: 1,
      mounted: false,
      observer: null
    }
  },

  mounted() {
    if (this.static) {
      this.mounted = true
      return
    }

    this.observer = new ResizeObserver(() => {
      const list = this.$el.querySelector('ul')
      if (list) {
        const children = Array.from(list.children)
        const size = children
          .slice(0, this.contents.length)
          .reduce((sum, el) => sum + el.getBoundingClientRect().width, 0)
        this.$el.style.setProperty('--content-size-inline', `${size}px`)
        this.iterations = Math.ceil(this.$el.clientWidth / size) * 2
        this.mounted = true
      }
    })

    this.$el.querySelectorAll('ul, li').forEach(el => {
      this.observer.observe(el)
    })
  },

  beforeDestroy() {
    this.observer?.disconnect()
  }
}
</script>

<style scoped>
@keyframes text-scroll {
  to {
    translate: calc(-1 * var(--content-size-inline, 0px)) 0;
  }
}

@keyframes wave-scroll {
  from {
    translate: var(--wave-size-inline) 0;
  }
}

aside {
  --wave-size-block: 0.75rem;
  --wave-size-inline: max(100%, calc(var(--wave-size-block) * 18));

  inline-size: 100%;
  margin-block-end: calc(-1 * var(--wave-size-block));
  overflow: hidden;
  padding-block-end: var(--wave-size-block);
  position: relative;
  transition: opacity 300ms ease-out;

  @apply z-global-banner;
}

aside::before,
aside::after {
  animation: 13s linear wave-scroll infinite;
  background-color: var(--banner-color-background, #1d2a58);
  block-size: var(--wave-size-block);
  bottom: 2px;
  content: '';
  inline-size: calc(var(--wave-size-inline) + 1px);
  mask: url('data:image/svg+xml;utf8,<svg width="216" height="12" viewBox="0 0 216 12" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg"><path d="M216 4c-44 0-64 8-108 8S44 4 0 4V0h216v4Z" fill="black"/></svg>')
    0 0 / 20% 100% repeat-x;
  position: absolute;
}

aside::before {
  left: 0;
  scale: -1 1;
  transform-origin: left center;
}

aside::after {
  left: -0.5px;
}

aside:not(.banner--mounted) {
  opacity: 0;
}

p {
  align-items: center;
  background-color: var(--banner-color-background, #1d2a58);
  color: var(--banner-color-text, #fff);
  display: flex;
  justify-content: center;
}

ul {
  display: grid;
  grid-auto-columns: max-content;
  grid-auto-flow: column;
  inline-size: fit-content;
  max-inline-size: 100%;
}

.banner--mounted ul {
  animation: 90s linear text-scroll infinite;
}

li {
  background-color: var(--banner-color-background, #1d2a58);
  color: var(--banner-color-text, #fff);
  line-height: 1;
  list-style-position: inside;
  list-style-type: '\2190';
  text-align: center;
}

li::marker {
  font-family: theme('fontFamily.antidote');
}

>>> a,
>>> span {
  font-size: 0.9375rem;
}

>>> a {
  text-decoration: underline;
}

>>> :where(a, span):not(span > *) {
  display: inline-block;
  padding: 0.5rem 2.5rem;
}
</style>
